import { compareDates, type LocalDate } from '@tahi/core';
import { copy } from '../copy';
import { shortDate } from '../format';

const W = 320;
const H = 132;
const PAD_TOP = 8;
const PAD_BOTTOM = 4;

interface Props {
  days: { date: LocalDate; count: number | null; limit: number | null }[];
  today: LocalDate;
  quitDate: LocalDate | null;
}

/**
 * The taper at a glance (spec: progress, daily counts chart): the daily limit as an amber step line
 * down to the quit date, each day's count as a column up to today, and a marker on today. One
 * labelled image; the numbers are also in text on the same screen.
 */
export function TaperChart({ days, today, quitDate }: Props) {
  if (days.length === 0) return null;
  const max = Math.max(1, ...days.map((d) => Math.max(d.count ?? 0, d.limit ?? 0)));
  const slot = W / days.length;
  const y = (v: number) => PAD_TOP + (1 - v / max) * (H - PAD_TOP - PAD_BOTTOM);
  const todayIndex = days.findIndex((d) => d.date === today);
  // The limit as horizontal steps (one per day), joined where it changes.
  let path = '';
  days.forEach((d, i) => {
    if (d.limit === null) return;
    const x0 = i * slot;
    const x1 = (i + 1) * slot;
    const yy = y(d.limit).toFixed(1);
    const prev = i > 0 ? days[i - 1]!.limit : null;
    path += prev === null ? `M${x0.toFixed(1)},${yy}` : `L${x0.toFixed(1)},${yy}`;
    path += ` L${x1.toFixed(1)},${yy} `;
  });
  const first = days[0]!;
  const last = days[days.length - 1]!;
  const start = first.limit;
  const over = days.filter(
    (d) =>
      d.count !== null && d.limit !== null && d.count > d.limit && compareDates(d.date, today) < 0,
  ).length;
  const label = [
    copy.taperChartLabel(shortDate(first.date), start, quitDate ? shortDate(quitDate) : null),
    over > 0 ? copy.daysOverLabel(over) : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <figure role="img" aria-label={label} className="flex flex-col gap-1">
      <div className="flex gap-2">
        <div
          aria-hidden="true"
          className="flex w-6 shrink-0 flex-col justify-between text-right font-mono text-[11px] tabular-nums text-muted"
          style={{ height: H }}
        >
          <span>{max}</span>
          <span>0</span>
        </div>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="min-w-0 flex-1"
          style={{ height: H }}
          aria-hidden="true"
        >
          <line x1={0} x2={W} y1={H - PAD_BOTTOM} y2={H - PAD_BOTTOM} stroke="var(--divider)" />
          {days.map((d, i) =>
            d.count === null || d.count === 0 ? null : (
              <rect
                key={d.date}
                x={i * slot + slot * 0.15}
                width={Math.max(0.8, slot * 0.7)}
                y={y(d.count)}
                height={H - PAD_BOTTOM - y(d.count)}
                rx={Math.min(2, slot * 0.2)}
                fill={d.limit !== null && d.count > d.limit ? 'var(--chart-6)' : 'var(--chart-1)'}
              />
            ),
          )}
          <path
            d={path}
            fill="none"
            stroke="var(--chart-2)"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
          {todayIndex >= 0 ? (
            <line
              x1={(todayIndex + 0.5) * slot}
              x2={(todayIndex + 0.5) * slot}
              y1={0}
              y2={H - PAD_BOTTOM}
              stroke="var(--fg)"
              strokeDasharray="3 3"
              strokeOpacity={0.5}
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
        </svg>
      </div>
      <figcaption
        aria-hidden="true"
        className="ml-8 flex justify-between gap-2 text-[11px] text-muted"
      >
        <span>{shortDate(first.date)}</span>
        {todayIndex > 0 && todayIndex < days.length - 1 ? <span>{copy.today}</span> : null}
        <span>{quitDate && last.date === quitDate ? copy.quitDay : shortDate(last.date)}</span>
      </figcaption>
      <ul
        aria-hidden="true"
        className="ml-8 mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted"
      >
        <li className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-4 bg-[var(--chart-2)]" />
          {copy.legendLimit}
        </li>
        <li className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[var(--chart-1)]" />
          {copy.legendSmoked}
        </li>
        <li className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[var(--chart-6)]" />
          {copy.legendOver}
        </li>
      </ul>
    </figure>
  );
}
